import { formatDistanceToNow } from 'date-fns';
import { ExternalLink, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/atoms/dialog';
import { MAP_DETAIL_CARD_WIDTH } from '@/features/locations/constants';
import { DEAL_STAGES, dealStageStyle } from '@/features/locations/constants/dealStages';
import {
  MAP_INFO_CARD_BODY_CLASS,
  MAP_INFO_CARD_CLASS,
} from '@/features/map/components/mapBarGeometry';
import { cn } from '@/libs/utils/cn';
import { formatOwnerName } from '@/libs/utils/formatOwnerName';
import { THIN_SCROLLBAR } from '@/libs/utils/thinScrollbar';
import type { IDealList, IDealSummary, IZone } from '@/types/api';
import { COUNTRY_FLAGS, COUNTRY_LABELS } from '../constants/markets';
import { isSafeHttpUrl } from '../utils/isSafeHttpUrl';

interface ZoneMapCardProps {
  /** The selected zone, already fetched by the surface. */
  zone: IZone;
  /**
   * The mirrored set the shell already polls for the map source; undefined until it answers. Passed
   * in rather than fetched here so selecting a zone opens no second observer on the payload.
   */
  deals?: IDealList;
  /** Opens that deal's card and closes this one. They share the left slot. */
  onSelectDeal: (dealId: string) => void;
  onClose: () => void;
  /** Overlay host. The map chrome, so the card sits in the map viewport. */
  dialogContainer?: HTMLElement;
}

interface StageGroup {
  stage: string;
  color: string;
  deals: IDealSummary[];
}

/**
 * Groups a zone's deals by stage, in the pipeline's order. A stage HubSpot renamed or added is kept
 * and sorted last rather than dropped: HubSpot owns the vocabulary, so an unknown label means the
 * mirror is ahead of this list, not that the deal should disappear.
 */
function groupByStage(deals: IDealSummary[], zoneName: string): StageGroup[] {
  const byStage = new Map<string, IDealSummary[]>();
  for (const deal of deals) {
    if (deal.zone_ref !== zoneName) continue;
    const group = byStage.get(deal.stage);
    if (group) group.push(deal);
    else byStage.set(deal.stage, [deal]);
  }

  const rank = (stage: string) => {
    const known = DEAL_STAGES.indexOf(stage);
    return known === -1 ? DEAL_STAGES.length : known;
  };

  return [...byStage.entries()]
    .sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b))
    .map(([stage, group]) => ({
      stage,
      color: dealStageStyle(stage).color,
      deals: group,
    }));
}

/** One labelled fact about the zone. */
function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-2.5">
      <span className="text-[#626968]">{label}</span>
      {children}
    </div>
  );
}

/**
 * The selected zone, pinned to the map's top-left in the same slot as a deal, location, or place.
 * Non-modal and overlay-less: the map stays live, so a user can pan and click pins while it is
 * open. Read-only: zones are owned by Notion. Opening a deal closes this card.
 */
export function ZoneMapCard({
  zone,
  deals: data,
  onSelectDeal,
  onClose,
  dialogContainer,
}: ZoneMapCardProps) {
  // Held still until the mirror answers: an empty state before the first response would read as
  // "this zone has no deals" when the truth is "we have not looked yet".
  const groups = data ? groupByStage(data.deals, zone.name) : null;
  const total = groups?.reduce((sum, group) => sum + group.deals.length, 0) ?? 0;
  const notionUrl =
    zone.notion_page_url != null && isSafeHttpUrl(zone.notion_page_url)
      ? zone.notion_page_url
      : null;

  return (
    // Escape is the surface's: it closes the zone with or without a card to press it in.
    <Dialog open modal={false}>
      <DialogContent
        dialogContainer={dialogContainer}
        showOverlay={false}
        showClose={false}
        position="tl"
        className={cn(
          MAP_INFO_CARD_CLASS,
          'border-0 bg-transparent p-0 shadow-none',
          MAP_DETAIL_CARD_WIDTH,
        )}
        onInteractOutside={(event) => {
          // Non-modal: a click on the map must not dismiss the card.
          event.preventDefault();
        }}
      >
        <div
          className={cn(
            MAP_INFO_CARD_BODY_CLASS,
            THIN_SCROLLBAR,
            'flex h-fit max-h-full w-full flex-col rounded-[14px] border border-[#E5E6E6] bg-white p-3.5 shadow-[0_10px_26px_rgba(25,84,94,0.18),0_2px_6px_rgba(25,84,94,0.1)]',
          )}
        >
          <div className="flex shrink-0 items-start justify-between gap-2">
            <DialogTitle
              className="min-w-0 flex-1 truncate text-[15px] font-bold leading-5 text-[#021715]"
              title={zone.name}
            >
              {zone.name}
            </DialogTitle>
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="rounded-md p-0.5 text-[#626968] hover:text-[#021715] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="size-3.5" aria-hidden />
            </button>
          </div>

          <div className="mt-[9px] flex shrink-0 flex-col gap-1.5 text-xs leading-4">
            <DetailRow label="Country">
              {zone.country !== null ? (
                <span className="text-right font-bold text-[#021715]">
                  <span data-testid="country-flag" aria-hidden>
                    {COUNTRY_FLAGS[zone.country]}
                  </span>{' '}
                  {COUNTRY_LABELS[zone.country] ?? zone.country}
                </span>
              ) : (
                <span className="italic text-[#626968]">Not set</span>
              )}
            </DetailRow>

            <DetailRow label="Owner">
              {zone.owner_id !== null ? (
                <span className="truncate font-bold text-[#021715]" title={zone.owner_id}>
                  {formatOwnerName(zone.owner_id)}
                </span>
              ) : (
                <span className="italic text-[#626968]">Unassigned</span>
              )}
            </DetailRow>
          </div>

          {notionUrl !== null && (
            <a
              href={notionUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-[13px] inline-flex shrink-0 items-center justify-center gap-[5px] self-start rounded-full border border-[#E5E6E6] px-2.5 py-[7px] text-[11px] leading-4 text-[#006369] hover:bg-[#EBFBF9]"
            >
              Open in Notion
              <ExternalLink className="size-[11px]" aria-hidden />
            </a>
          )}

          <div className="mt-3 flex min-h-0 flex-1 flex-col gap-2 border-t border-[#F2F2F2] pt-[11px]">
            <div className="flex shrink-0 items-baseline justify-between gap-2">
              <span className="text-[9.5px] font-bold uppercase leading-none tracking-[0.08em] text-[#626968]">
                Pipeline
              </span>
              <span className="text-[11px] leading-[15px] text-[#626968]">
                {groups ? `${total} deal${total === 1 ? '' : 's'}` : 'Loading deals'}
              </span>
            </div>

            {groups && groups.length === 0 && (
              <p className="text-xs text-[#626968]">No deal attached to this zone.</p>
            )}

            {groups && groups.length > 0 && (
              <ul className="flex flex-col gap-3">
                {groups.map((group) => (
                  <li
                    key={group.stage}
                    data-testid={`zone-pipeline-stage-${group.stage}`}
                    data-stage={group.stage}
                    className="shrink-0"
                  >
                    <div
                      data-testid="zone-pipeline-stage"
                      data-stage={group.stage}
                      className="flex items-center gap-2"
                    >
                      <span
                        aria-hidden
                        className="size-2 shrink-0 rounded-full"
                        style={{ backgroundColor: group.color }}
                      />
                      <span className="flex-1 truncate text-xs font-medium text-[#021715]">
                        {group.stage}
                      </span>
                      <span className="text-xs tabular-nums text-[#626968]">
                        {group.deals.length}
                      </span>
                    </div>
                    <ul className="mt-1 flex flex-col gap-px pl-4">
                      {group.deals.map((deal) => (
                        <li key={deal.id} className="flex">
                          <button
                            type="button"
                            onClick={() => onSelectDeal(deal.id)}
                            className="block min-h-6 flex-1 overflow-hidden rounded px-1.5 py-1 text-left text-xs leading-4 text-ellipsis whitespace-nowrap text-[#021715]/70 transition-colors hover:bg-[#F2F2F2]"
                            title={deal.name}
                          >
                            {deal.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {data && (
            <span className="mt-3 shrink-0 text-[11px] leading-[15px] text-[#626968]">
              Synced {formatDistanceToNow(new Date(data.synced_at), { addSuffix: true })}
            </span>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
